import { useQuery } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useState } from 'react';
import { api, ApiError, type Me } from './lib/api.ts';
import Layout from './components/Layout.tsx';
import Login from './pages/Login.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Chargers from './pages/Chargers.tsx';
import ChargerDetail from './pages/ChargerDetail.tsx';
import Economy from './pages/Economy.tsx';
import Register from './pages/Register.tsx';
import System from './pages/System.tsx';

export default function App() {
  const meQuery = useQuery<Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
  });

  if (meQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500">
        Laster …
      </div>
    );
  }

  const me = meQuery.data ?? null;
  if (!me) {
    return <Login onLoggedIn={() => meQuery.refetch()} />;
  }

  // Innlogget, men medlem av flere tenants og ingen valgt ennå
  if (me.rolle === null) {
    return <TenantPicker me={me} onValgt={() => meQuery.refetch()} />;
  }

  const erSuperadmin = me.rolle === 'sysadmin';
  const harTenant = !!me.tenantId;

  return (
    <Layout me={me}>
      <Routes>
        {erSuperadmin && <Route path="/system" element={<System />} />}
        {(!erSuperadmin || harTenant) && (
          <>
            <Route path="/" element={<Dashboard me={me} />} />
            <Route path="/ladere" element={<Chargers me={me} />} />
            <Route path="/ladere/:id" element={<ChargerDetail me={me} />} />
            <Route path="/okonomi" element={<Economy me={me} />} />
            {(me.rolle === 'admin' || erSuperadmin) && (
              <Route path="/register" element={<Register />} />
            )}
          </>
        )}
        <Route
          path="*"
          element={<Navigate to={erSuperadmin && !harTenant ? '/system' : '/'} replace />}
        />
      </Routes>
    </Layout>
  );
}

function TenantPicker({ me, onValgt }: { me: Me; onValgt: () => void }) {
  const [feil, setFeil] = useState<string | null>(null);

  async function velg(tenantId: string) {
    try {
      await api.post('/auth/switch-tenant', { tenantId });
      onValgt();
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Kunne ikke velge tenant');
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-8 shadow-lg ring-1 ring-slate-200">
        <div className="mb-6 text-center">
          <div className="text-3xl">⚡</div>
          <h1 className="mt-2 text-2xl font-bold text-slate-800">Velg tenant</h1>
          <p className="mt-1 text-sm text-slate-500">
            {me.navn}, du er medlem av flere — hvilken vil du jobbe i?
          </p>
        </div>
        <div className="space-y-2">
          {me.tenants.map((t) => (
            <button
              key={t.id}
              onClick={() => velg(t.id)}
              className="flex w-full items-center justify-between rounded-lg border border-slate-300 px-4 py-3 text-left hover:border-blue-500 hover:bg-blue-50"
            >
              <span className="font-medium text-slate-800">{t.navn}</span>
              <span className="text-xs text-slate-400">{t.rolle}</span>
            </button>
          ))}
        </div>
        {feil && <p className="mt-3 text-sm text-red-600">{feil}</p>}
      </div>
    </div>
  );
}
